'use client'

// React
import React from 'react'

// Animation
import { motion } from 'framer-motion'
import { staggerContainer, fadeInUp } from './motion-variants'

// UI components
import { spaceGrotesk, TextH2 } from './typography'
import { Body } from '../typography/text'

// Utils
import { Join } from '../../lib/utils'

// ── Utilities ────────────────────────────────────────────────────────────────

/** Split a string into individual word tokens. */
export function splitWords(text: string): string[] {
  return text.split(/\s+/).filter(Boolean)
}

/**
 * Flatten a ReactNode (e.g. title) into an ordered list of word strings and preserved elements (e.g. <br />).
 */
function flattenToTokens(
  node: React.ReactNode,
): Array<string | React.ReactElement> {
  if (typeof node === 'string') return splitWords(node)
  if (React.isValidElement(node)) {
    if (node.type === 'br') return [node]
    const { children } = node.props as { children?: React.ReactNode }
    return children ? flattenToTokens(children) : []
  }
  if (Array.isArray(node)) return node.flatMap(flattenToTokens)
  return []
}

// Stagger for title words: when section enters view, words fade in one by one (once).
const titleWordsContainer = {
  initial: {},
  animate: {
    transition: { staggerChildren: 0.12, delayChildren: 0 },
  },
}

const titleWord = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.6, ease: 'easeOut' as const },
  },
}

// Full text for screen readers (tokens joined with spaces; <br /> becomes space).
function tokensToSrText(tokens: Array<string | React.ReactElement>): string {
  return tokens
    .map((t) => (typeof t === 'string' ? t : ' '))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// ── Staggered title words (in-view, once) ─────────────────────────────────────

export function TitleWords({ children }: { children: React.ReactNode }) {
  const tokens = flattenToTokens(children)
  const srText = tokensToSrText(tokens)
  return (
    <>
      <span className="sr-only">{srText}</span>
      <motion.span
        aria-hidden="true"
        variants={titleWordsContainer}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true, margin: '-200px 0px' }}
        style={{ display: 'inline' }}
      >
        {tokens.map((token, i) =>
          typeof token === 'string' ? (
            <motion.span
              key={`${token}-${i}`}
              variants={titleWord}
              className="mr-[0.28em] inline-block last:mr-0"
            >
              {token}
            </motion.span>
          ) : (
            <React.Fragment key={`br-${i}`}>{token}</React.Fragment>
          ),
        )}
      </motion.span>
    </>
  )
}

// ── SectionHeader ─────────────────────────────────────────────────────────────

interface SectionHeaderProps {
  title: React.ReactNode
  subtitle?: React.ReactNode
  /** Optional extra classes for the subtitle paragraph (merged with defaults via Join) */
  subtitleClassName?: string
  description?: React.ReactNode
  pretitle?: React.ReactNode
  pretitleColor?: string
  /** Full heading as single string for crawlers and screen readers (e.g. "Underlying Technologies Neurosymbolic AI for Code") */
  srOnlyFullHeading?: string
  className?: string
}

export function SectionHeader({
  title,
  subtitle,
  subtitleClassName,
  description,
  pretitle,
  pretitleColor = 'text-cyan-500/80',
  srOnlyFullHeading,
  className = 'relative z-10',
}: SectionHeaderProps) {
  return (
    <motion.div
      variants={staggerContainer}
      initial="initial"
      whileInView="animate"
      viewport={{ once: true, margin: '-200px 0px' }}
      className={Join(
        'mx-auto max-w-6xl',
        description
          ? 'grid grid-cols-1 items-start md:grid-cols-2 md:gap-3'
          : '',
        className,
      )}
    >
      {srOnlyFullHeading ? (
        <span className="sr-only">{srOnlyFullHeading}</span>
      ) : null}
      {pretitle && (
        <motion.div
          variants={fadeInUp}
          className={Join(
            'col-span-full mb-3 font-mono text-xs tracking-widest uppercase',
            pretitleColor,
          )}
        >
          {pretitle}
        </motion.div>
      )}
      <motion.div
        variants={fadeInUp}
        className={Join(subtitle ? 'relative flex flex-col gap-2' : '')}
      >
        <TextH2
          fontFamily={spaceGrotesk.className}
          colors="text-blue-900 dark:text-white"
          text="text-4xl lg:text-5xl leading-snug"
        >
          <TitleWords>{title}</TitleWords>
        </TextH2>
        {subtitle && (
          <Body size="xl" tone="muted" className="mt-3 lg:mt-4">
            <span className={subtitleClassName}>{subtitle}</span>
          </Body>
        )}
      </motion.div>
      {description && (
        <motion.div variants={fadeInUp} className="relative z-10">
          {typeof description === 'string' ? (
            <Body size="xl" tone="muted" className="mt-3 lg:mt-4">
              {description}
            </Body>
          ) : (
            description
          )}
        </motion.div>
      )}
    </motion.div>
  )
}
